import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const getBaseUrl = () => {
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");
  }
  return "https://lirkprint-backend.onrender.com";
};

const API = `${getBaseUrl()}/api`;
const AuthContext = createContext();

// Axios request interceptor to attach Bearer token if present
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("lirkprint_token");
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => Promise.reject(error));

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem("lirkprint_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers,
      });
      setUser(data);
    } catch {
      localStorage.removeItem("lirkprint_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    if (data?.token) {
      localStorage.setItem("lirkprint_token", data.token);
    }
    setUser(data);
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
    if (data?.token) {
      localStorage.setItem("lirkprint_token", data.token);
    }
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem("lirkprint_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true, headers });
    } catch {
      // Ignore network errors on logout
    } finally {
      localStorage.removeItem("lirkprint_token");
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
